/**
 * Rich stream summary card on a team: KPIs, proportional bar, charts, pause/resume, and deep-link to stream detail.
 *
 * Where: grid of streams on `TeamDetails`.
 * Uses: `teamApi` stream mutations, `ProportionalProgressBar`, `ProspectEventsLinearChart`.
 * Behavior: expand/collapse on mobile; confirmation dialogs for status changes; loads chart data when expanded.
 */
/**
 * Rich stream summary card under a team: KPIs, proportional bar, charts, operator selector, pause/resume.
 *
 * Where: grid of streams on `TeamDetails`.
 * Uses: `teamApi` stream mutations, `ProportionalProgressBar`, `ProspectEventsLinearChart`.
 * Behavior: expand/collapse stats; confirmation dialogs for destructive actions; responsive layout.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { 
  BarChart as BarChartIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  HelpOutline as HelpOutlineIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProportionalProgressBar } from '@/components';
import { ProspectEventsLinearChart } from '@/components/charts/ProspectEventsLinearChart';
import { teamApi } from '@/api/team';
import { useNavigate } from 'react-router-dom';

interface UserItem {
  key: string;
  name?: string | null;
  email?: string | null;
  profile_image?: string | null;
  is_unipile_connected?: boolean;
  premium_features?: string[];
}

interface TeamRoleItem {
  key: string;
  name: string;
  user: UserItem;
}

interface TeamItem {
  key: string;
  name: string;
  owner?: UserItem | null;
  roles?: TeamRoleItem[];
}

interface StreamItem {
  key: string;
  name: string;
  status: string;
  created_at: string;
  notes?: string | null;
  operator?: UserItem | null;
  creator?: UserItem | null;
  team?: TeamItem | null;
  statistics?: {
    total_leads?: number;
    scheduled_meetings?: number;
    scheduled_meetings_since_yesterday?: number;
    acceptance_rate?: number; // percentage (0-100)
    response_rate?: number; // percentage (0-100)
    sent_invites?: number;
    sent_invites_yesterday?: number;
    sent_invites_day_before?: number;
    sent_messages?: number;
    accepted_connections?: number;
    waiting_connections?: number;
    messages_received?: number;
    failed_jobs?: number;
    failed_jobs_yesterday?: number;
    prospect_estimation_days?: number;
    progress_estimation_days?: number;
    progress_sections?: Array<{
      category: string;
      count: number;
      color?: string;
    }>;
    accepted_connections_yesterday?: number;
    accepted_connections_day_before?: number;
    accepted_meetings_yesterday?: number;
    accepted_meetings_day_before?: number;
    positive_responses_yesterday?: number;
    positive_responses_day_before?: number;
    meeting_accepted_count?: number;
  };
}

interface StreamCardProps {
  stream: StreamItem;
}

const TrendIndicator: React.FC<{ current: number; previous: number }> = ({ current, previous }) => {
  if (current > previous) {
    return <TrendingUpIcon sx={{ fontSize: '0.875rem', color: 'success.main', ml: 0.5 }} />;
  }
  if (current < previous) {
    return <TrendingDownIcon sx={{ fontSize: '0.875rem', color: 'error.main', ml: 0.5 }} />;
  }
  return <TrendingFlatIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', ml: 0.5 }} />;
};

const HintIcon: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top">
    <HelpOutlineIcon sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5, cursor: 'help' }} />
  </Tooltip>
);

/**
 * OverlappingAvatarStack component - displays up to 5 avatars overlapping
 */
interface OverlappingAvatarStackProps {
  members: Array<{ profile_image?: string | null; name?: string | null; email?: string | null }>;
  totalCount: number;
  maxVisible?: number;
  size?: number;
  overlap?: number;
}

const OverlappingAvatarStack: React.FC<OverlappingAvatarStackProps> = ({
  members,
  totalCount,
  maxVisible = 5,
  size = 30,
  overlap = 15,
}) => {
  const visibleMembers = members.slice(0, maxVisible);
  const remainingCount = totalCount > maxVisible ? totalCount - maxVisible : 0;

  const getInitials = (name: string | null | undefined, email: string | null | undefined): string => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '?';
  };

  const getDisplayName = (name: string | null | undefined, email: string | null | undefined): string => {
    if (name) {
      return name;
    }
    if (email) {
      return email;
    }
    return 'Unknown';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', height: size }}>
      {visibleMembers.map((member, index) => (
        <Tooltip
          key={index}
          title={getDisplayName(member.name, member.email)}
          placement="top"
          arrow
        >
          <Avatar
            src={member.profile_image || undefined}
            alt={member.name || member.email || 'Member'}
            sx={{
              width: size,
              height: size,
              fontSize: size * 0.4,
              border: '2px solid',
              borderColor: 'background.paper',
              position: index === 0 ? 'relative' : 'absolute',
              left: index === 0 ? 0 : `${index * (size - overlap)}px`,
              zIndex: maxVisible - index,
              bgcolor: 'primary.main',
              cursor: 'pointer',
            }}
          >
            {getInitials(member.name, member.email)}
          </Avatar>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Tooltip
          title={`${remainingCount} more ${remainingCount === 1 ? 'member' : 'members'}`}
          placement="top"
          arrow
        >
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: size * 0.4,
              border: '2px solid',
              borderColor: 'background.paper',
              position: 'absolute',
              left: `${visibleMembers.length * (size - overlap)}px`,
              zIndex: 0,
              bgcolor: 'grey.400',
              color: 'common.white',
              cursor: 'pointer',
            }}
          >
            +{remainingCount}
          </Avatar>
        </Tooltip>
      )}
    </Box>
  );
};

const PROGRESS_BAR_CATEGORIES: string[] = [
  'Queuing for import',
  'Checking if already connected',
  'Sending invite',
  'Pending acceptance',
  'Queuing for follow-up',
];

// Helper function to generate progress segments from stream statistics.
// Always returns all 4 categories (with 0 for missing) so numbers are always shown.
const generateProgressSegments = (stream: StreamItem) => {
  const sectionMap = new Map<string, { count: number; color?: string }>();
  if (stream.statistics?.progress_sections) {
    for (const section of stream.statistics.progress_sections) {
      if (PROGRESS_BAR_CATEGORIES.includes(section.category)) {
        sectionMap.set(section.category, {
          count: section.count ?? 0,
          color: section.color,
        });
      }
    }
  }
  return PROGRESS_BAR_CATEGORIES.map((category) => {
    const data = sectionMap.get(category);
    const value = data?.count ?? 0;
    return {
      value,
      description: `${category}: ${value} prospects`,
      color: data?.color,
    };
  });
};

// Helper component to render operator with connection status and premium features
const OperatorDisplay: React.FC<{ 
  operator: UserItem; 
  onNavigate: (e: React.MouseEvent) => void;
}> = ({ operator, onNavigate }) => {
  const isConnected = operator.is_unipile_connected ?? false;
  const premiumFeatures = operator.premium_features || [];
  
  // Map premium features to display names
  const getFeatureDisplayName = (feature: string): string => {
    const featureMap: Record<string, string> = {
      'sales_navigator': 'Sales Navigator',
      'recruiter': 'Recruiter',
      'premium': 'Premium',
    };
    return featureMap[feature] || feature;
  };
  
  // Get first letter of feature for badge
  const getFeatureBadge = (feature: string): string => {
    return feature.charAt(0).toUpperCase();
  };

  // Get color for premium feature badge
  const getFeatureColor = (feature: string): string => {
    const colorMap: Record<string, string> = {
      'sales_navigator': 'info.main',
      'recruiter': 'success.main',
      'premium': 'warning.main',
    };
    return colorMap[feature] || 'primary.main';
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.5,
        cursor: 'pointer',
        '&:hover': { opacity: 0.7 }
      }}
      onClick={onNavigate}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
        Operator:
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
        {operator.name || operator.email || 'Unknown'}
      </Typography>
      {!isConnected && (
        <Tooltip title="LinkedIn account is disconnected" placement="top" arrow>
          <WarningIcon sx={{ fontSize: '1rem', color: 'error.main', ml: 0.25, fontWeight: 'bold' }} />
        </Tooltip>
      )}
      {isConnected && premiumFeatures.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.25 }}>
          {premiumFeatures.map((feature) => (
            <Tooltip key={feature} title={getFeatureDisplayName(feature)} placement="top" arrow>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: getFeatureColor(feature),
                  color: 'common.white',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  cursor: 'help',
                }}
              >
                {getFeatureBadge(feature)}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Helper function to get stream statistics in a convenient form
const getStreamStats = (stream: StreamItem) => {
  const s = stream.statistics || {};
  const totalLeads = s.total_leads || 0;
  const acceptanceRate = (s.acceptance_rate || 0) / 100; // convert to 0-1
  const responseRate = (s.response_rate || 0) / 100; // convert to 0-1
  const acceptedConnections = s.accepted_connections || 0;
  const messagesReceived = s.messages_received || 0;

  return {
    totalLeads,
    scheduledMeetings: s.scheduled_meetings || 0,
    scheduledMeetingsSinceYesterday: s.scheduled_meetings_since_yesterday || 0,
    meetingAcceptedCount: s.meeting_accepted_count || 0,
    sentMessages: s.sent_messages || 0,
    acceptanceRate,
    acceptedConnections,
    responseRate,
    messagesReceived,
    failedJobs: s.failed_jobs || 0,
    failedJobsYesterday: s.failed_jobs_yesterday || 0,
    prospectEstimationDays: s.prospect_estimation_days || 0,
    sentInvites: s.sent_invites || 0,
  };
};

// Helper function to get warnings for a stream
const getStreamWarnings = (stream: StreamItem, stats: ReturnType<typeof getStreamStats>) => {
  const warnings: Array<{ message: string; severity: 'error' | 'warning' }> = [];
  
  // Check for disconnected account
  if (stream.operator && !stream.operator.is_unipile_connected) {
    warnings.push({
      message: 'LinkedIn account is disconnected',
      severity: 'error',
    });
  }
  
  // Check for remaining days < 7 (including 0 which means no active sending pipeline).
  if (stats.prospectEstimationDays < 7) {
    warnings.push({
      message: stats.prospectEstimationDays <= 0
        ? 'No remaining prospects — consider adding more prospects'
        : `Less than ${Math.round(stats.prospectEstimationDays)} days remaining to process all prospects`,
      severity: 'warning',
    });
  }

  // Check for failed jobs > 5% of total leads
  if (stats.totalLeads > 0 && stats.failedJobs > 0) {
    const failedPercentage = (stats.failedJobs / stats.totalLeads) * 100;
    if (failedPercentage > 5) {
      warnings.push({
        message: `High failure rate: ${failedPercentage.toFixed(1)}% of prospects failed (${stats.failedJobs} out of ${stats.totalLeads})`,
        severity: 'error',
      });
    }
  }
  
  return warnings;
};

export const StreamCard: React.FC<StreamCardProps> = ({ stream }) => {
  const stats = getStreamStats(stream);
  const progressSegments = generateProgressSegments(stream);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const queryClient = useQueryClient();

  // Optimistic update: maintain local state for immediate UI feedback
  const [localStatus, setLocalStatus] = useState(stream.status);

  // Sync localStatus when stream prop changes (from parent refetch)
  useEffect(() => {
    setLocalStatus(stream.status);
  }, [stream.status]);

  // Update mutation with query invalidation
  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: string) => teamApi.updateStream(stream.key, { status: newStatus }),
    onMutate: async (newStatus: string) => {
      // Optimistically update local state immediately
      setLocalStatus(newStatus);
      
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['experiments'] });
    },
    onSuccess: () => {
      // Invalidate and refetch experiments queries to sync with server
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      // Also invalidate any stream-specific queries
      queryClient.invalidateQueries({ queryKey: ['stream', stream.key] });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
    },
    onError: (error) => {
      // Revert optimistic update on error
      setLocalStatus(stream.status);
      console.error('Failed to update stream status:', error);
    },
  });

  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === 'WITHDRAW') {
      setIsWithdrawDialogOpen(true);
      return;
    }
    updateStatusMutation.mutate(newStatus);
  };

  const handleWithdrawConfirm = () => {
    setIsWithdrawDialogOpen(false);
    updateStatusMutation.mutate('WITHDRAW');
  };

  const handleCardClick = () => {
    // Don't navigate if chart is expanded
    if (isExpanded) return;
    navigate(`/campaigns/${stream.key}`);
  };

  // Get team members from team roles
  const teamMembers = stream.team?.roles?.map((role: TeamRoleItem) => role.user) || [];
  const memberCount = teamMembers.length;

  if (isMobile) {
    return (
      <Card sx={{ 
        cursor: isExpanded ? 'default' : 'pointer', 
        '&:hover': { bgcolor: isExpanded ? 'transparent' : 'action.hover' }, 
        p: 2 
      }} onClick={handleCardClick}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {/* Warning Icons */}
              {getStreamWarnings(stream, stats).map((warning, index) => (
                <Tooltip key={index} title={warning.message} placement="top" arrow>
                  <WarningIcon 
                    sx={{ 
                      fontSize: '1.125rem', 
                      color: warning.severity === 'error' ? 'error.main' : 'warning.main',
                      flexShrink: 0,
                    }} 
                  />
                </Tooltip>
              ))}
              <Tooltip title={stream.name} placement="top">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaigns/${stream.key}`);
                  }}
                >
                  {stream.name}
                </Typography>
              </Tooltip>
              <IconButton
                size="small"
                sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'primary.main' }, flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/campaigns/${stream.key}`);
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            
            {/* Operator, Owner, Team, and Members */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 0.5 }}>
              {stream.operator && (
                <OperatorDisplay
                  operator={stream.operator}
                  onNavigate={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${stream.operator!.key}`);
                  }}
                />
              )}
              {stream.creator && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (stream.creator) {
                      navigate(`/users/${stream.creator.key}`);
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Owner:
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
                    {stream.creator.name || stream.creator.email || 'Unknown'}
                  </Typography>
                </Box>
              )}
              {stream.team && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (stream.team) {
                      navigate(`/teams/${stream.team.key}`);
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Team:
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
                    {stream.team.name}
                  </Typography>
                </Box>
              )}
              {memberCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      width: `${Math.min(memberCount, 5) * (24 - 12) + 24}px`, 
                      height: 24 
                    }}
                  >
                    <OverlappingAvatarStack
                      members={teamMembers}
                      totalCount={memberCount}
                      maxVisible={5}
                      size={24}
                      overlap={12}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl variant="standard" size="small" sx={{ minWidth: 100 }}>
              <Select
                value={stream.status}
                onChange={(e) => {
                  e.stopPropagation();
                  handleStatusChange(e.target.value as string);
                }}
                onClick={(e) => e.stopPropagation()}
                displayEmpty
                sx={{
                  '& .MuiSelect-select': { padding: '4px 8px', fontSize: '0.8125rem' },
                  '& .MuiSelect-icon': { color: 'text.secondary' },
                  '& .MuiSelect-root': {
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main' },
                  },
                }}
              >
                <MenuItem value="RUNNING">Running</MenuItem>
                <MenuItem value="PAUSED">Paused</MenuItem>
                <MenuItem value="DONE">Done</MenuItem>
                <MenuItem value="WAITING">Waiting</MenuItem>
                <MenuItem value="WITHDRAW">Withdraw</MenuItem>
                <MenuItem value="ARCHIVE">Archive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          {progressSegments.length > 0 ? (
            <ProportionalProgressBar segments={progressSegments} height={6} gap={3} showValues={true} />
          ) : (
            <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                No progress data available
              </Typography>
            </Box>
          )}
        </Box>

        {/* Lead Statistics */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                All leads
              </Typography>
              <HintIcon title="Total number of people in your prospect list" />
            </Box>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.875rem' }}>
              {stats.totalLeads}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Failed jobs
              </Typography>
              <HintIcon title={`Prospects that failed to process (${stats.failedJobsYesterday} failed yesterday)`} />
            </Box>
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500, fontSize: '0.875rem' }}>
              {stats.failedJobs}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Estimated days remaining
              </Typography>
              <HintIcon title="Estimated number of days until all prospects are processed" />
            </Box>
            <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem' }}>
              {Math.round(stats.prospectEstimationDays)} days
            </Typography>
          </Box>
        </Box>

        {/* Daily Trend Statistics */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Sent connections
              </Typography>
              <HintIcon title="Total connection requests sent" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                {stats.sentInvites}
              </Typography>
              <TrendIndicator 
                current={stream.statistics?.sent_invites_yesterday || 0} 
                previous={stream.statistics?.sent_invites_day_before || 0} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({stream.statistics?.sent_invites_yesterday || 0})
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Accepted connections
              </Typography>
              <HintIcon title={`People who accepted your connection requests (${stream.statistics?.accepted_connections_yesterday || 0} yesterday)`} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                {stats.acceptedConnections} ({(stats.acceptanceRate * 100).toFixed(1)}%)
              </Typography>
              <TrendIndicator 
                current={stream.statistics?.accepted_connections_yesterday || 0} 
                previous={stream.statistics?.accepted_connections_day_before || 0} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({stream.statistics?.accepted_connections_yesterday || 0})
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Responded messages
              </Typography>
              <HintIcon title={`Messages received from prospects (${stream.statistics?.positive_responses_yesterday || 0} yesterday)`} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                {stats.messagesReceived} ({(stats.responseRate * 100).toFixed(1)}%)
              </Typography>
              <TrendIndicator 
                current={stream.statistics?.positive_responses_yesterday || 0} 
                previous={stream.statistics?.positive_responses_day_before || 0} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({stream.statistics?.positive_responses_yesterday || 0})
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Accepted meetings
              </Typography>
              <HintIcon title="Chats tagged by AI as &quot;Meeting Accepted&quot;." />
            </Box>
            <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem' }}>
              {stats.meetingAcceptedCount} ({stats.messagesReceived > 0 ? ((stats.meetingAcceptedCount / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Scheduled meetings
              </Typography>
              <HintIcon title={`Total meetings scheduled (${stats.scheduledMeetingsSinceYesterday} new yesterday) - ${stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}% of responded messages`} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                {stats.scheduledMeetings} ({stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
              </Typography>
              <TrendIndicator 
                current={stats.scheduledMeetingsSinceYesterday} 
                previous={0} 
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({stats.scheduledMeetingsSinceYesterday})
              </Typography>
            </Box>
          </Box>
        </Box>

          {/* Statistics Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
            <Tooltip title={isExpanded ? "Hide Statistics" : "View Statistics"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                sx={{
                  color: isExpanded ? 'primary.main' : 'text.secondary',
                  '&:hover': { 
                    color: 'primary.main',
                    bgcolor: 'primary.50'
                  },
                  border: 1,
                  borderColor: isExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  gap: 0.5,
                }}
              >
                {isExpanded ? <CloseIcon fontSize="small" /> : <BarChartIcon fontSize="small" />}
                {isExpanded ? 'Hide' : 'Stats'}
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Expandable Chart Section */}
          <Collapse in={isExpanded}>
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
              <ProspectEventsLinearChart 
                streamUuid={stream.key} 
                enabled={isExpanded}
                height={300}
              />
            </Box>
          </Collapse>

        {/* Withdraw Confirmation Dialog */}
        <Dialog
          open={isWithdrawDialogOpen}
          onClose={() => setIsWithdrawDialogOpen(false)}
          onClick={(e) => e.stopPropagation()}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Withdraw all pending invites</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1">
              This will withdraw all pending connection invites for this experiment.
              A background job will process each pending invite and withdraw it.
              Once the withdrawal is complete, the experiment status will be set to <strong>Done</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone. Are you sure you want to proceed?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsWithdrawDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleWithdrawConfirm}
              disabled={updateStatusMutation.isPending}
            >
              Withdraw invites
            </Button>
          </DialogActions>
        </Dialog>
        </Card>
    );
  }

  // Desktop (existing layout sizing already aligned to project card)
  return (
    <Card
      sx={{
        p: 2,
        cursor: isExpanded ? 'default' : 'pointer',
        '&:hover': { bgcolor: isExpanded ? 'transparent' : 'action.hover' },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 0, pb: 0, '&:last-child': { pb: 0 } }}>
        {/* Stream Name Row - Separate row, full width */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', minWidth: 0, mb: 1 }}>
          {/* Warning Icons */}
          {getStreamWarnings(stream, stats).map((warning, index) => (
            <Tooltip key={index} title={warning.message} placement="top" arrow>
              <WarningIcon 
                sx={{ 
                  fontSize: '1.125rem', 
                  color: warning.severity === 'error' ? 'error.main' : 'warning.main',
                  flexShrink: 0,
                }} 
              />
            </Tooltip>
          ))}
          <Tooltip title={stream.name} placement="top">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' },
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/campaigns/${stream.key}`);
              }}
            >
              {stream.name}
            </Typography>
          </Tooltip>
          <IconButton
            size="small"
            sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'primary.main' }, flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/campaigns/${stream.key}`);
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content Row - Stats and details */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Stream Title and Progress */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 300 }}>
            {/* Operator, Owner, Team, and Members */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
              {stream.operator && (
                <OperatorDisplay
                  operator={stream.operator}
                  onNavigate={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${stream.operator!.key}`);
                  }}
                />
              )}
              {stream.creator && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (stream.creator) {
                      navigate(`/users/${stream.creator.key}`);
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Owner:
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
                    {stream.creator.name || stream.creator.email || 'Unknown'}
                  </Typography>
                </Box>
              )}
              {stream.team && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (stream.team) {
                      navigate(`/teams/${stream.team.key}`);
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Team:
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
                    {stream.team.name}
                  </Typography>
                </Box>
              )}
              {memberCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      width: `${Math.min(memberCount, 5) * (24 - 12) + 24}px`, 
                      height: 24 
                    }}
                  >
                    <OverlappingAvatarStack
                      members={teamMembers}
                      totalCount={memberCount}
                      maxVisible={5}
                      size={24}
                      overlap={12}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Progress Bar */}
            <Box sx={{ width: '100%', mt: 1 }}>
              {progressSegments.length > 0 ? (
                <ProportionalProgressBar
                  segments={progressSegments}
                  height={6}
                  gap={3}
                  showValues={true}
                />
              ) : (
                <Box sx={{ 
                  height: 6, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    No progress data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  All leads
                </Typography>
                <HintIcon title="Total number of people in your prospect list" />
              </Box>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.totalLeads}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Failed jobs
                </Typography>
                <HintIcon title={`Prospects that failed to process (${stats.failedJobsYesterday} failed yesterday)`} />
              </Box>
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.failedJobs}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Estimated days remaining
                </Typography>
                <HintIcon title="Estimated number of days until all prospects are processed" />
              </Box>
              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {Math.round(stats.prospectEstimationDays)} days
              </Typography>
            </Box>
          </Box>

          {/* Daily Trend Statistics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Sent connections
                </Typography>
                <HintIcon title="Total connection requests sent" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.sentInvites}
                </Typography>
                <TrendIndicator 
                  current={stream.statistics?.sent_invites_yesterday || 0} 
                  previous={stream.statistics?.sent_invites_day_before || 0} 
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({stream.statistics?.sent_invites_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Accepted connections
                </Typography>
                <HintIcon title={`People who accepted your connection requests (${stream.statistics?.accepted_connections_yesterday || 0} yesterday)`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.acceptedConnections} ({(stats.acceptanceRate * 100).toFixed(1)}%)
                </Typography>
                <TrendIndicator 
                  current={stream.statistics?.accepted_connections_yesterday || 0} 
                  previous={stream.statistics?.accepted_connections_day_before || 0} 
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({stream.statistics?.accepted_connections_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Responded messages
                </Typography>
                <HintIcon title={`Messages received from prospects (${stream.statistics?.positive_responses_yesterday || 0} yesterday)`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.messagesReceived} ({(stats.responseRate * 100).toFixed(1)}%)
                </Typography>
                <TrendIndicator 
                  current={stream.statistics?.positive_responses_yesterday || 0} 
                  previous={stream.statistics?.positive_responses_day_before || 0} 
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({stream.statistics?.positive_responses_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Accepted meetings
                </Typography>
                <HintIcon title="Chats tagged by AI as &quot;Meeting Accepted&quot;." />
              </Box>
              <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.meetingAcceptedCount} ({stats.messagesReceived > 0 ? ((stats.meetingAcceptedCount / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Scheduled meetings
                </Typography>
                <HintIcon title={`Total meetings scheduled (${stats.scheduledMeetingsSinceYesterday} new yesterday) - ${stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}% of responded messages`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.scheduledMeetings} ({stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
                </Typography>
                <TrendIndicator 
                  current={stats.scheduledMeetingsSinceYesterday} 
                  previous={0} 
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({stats.scheduledMeetingsSinceYesterday})
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Status and Meta */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 120 }}>
            <FormControl variant="standard" size="small" sx={{ minWidth: 100 }}>
              <Select
                value={localStatus}
                onChange={(e) => {
                  e.stopPropagation();
                  handleStatusChange(e.target.value as string);
                }}
                onClick={(e) => e.stopPropagation()}
                displayEmpty
                disabled={updateStatusMutation.isPending}
                sx={{
                  '& .MuiSelect-select': { padding: '4px 8px', fontSize: '0.8125rem' },
                  '& .MuiSelect-icon': { color: 'text.secondary' },
                  '& .MuiSelect-root': {
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main' },
                  },
                }}
              >
                <MenuItem value="RUNNING">Running</MenuItem>
                <MenuItem value="PAUSED">Paused</MenuItem>
                <MenuItem value="DONE">Done</MenuItem>
                <MenuItem value="WAITING">Waiting</MenuItem>
                <MenuItem value="WITHDRAW">Withdraw</MenuItem>
                <MenuItem value="ARCHIVE">Archive</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={isExpanded ? "Hide Statistics" : "View Statistics"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                  sx={{
                    color: isExpanded ? 'primary.main' : 'text.secondary',
                    '&:hover': { 
                      color: 'primary.main',
                      bgcolor: 'primary.50'
                    },
                    border: 1,
                    borderColor: isExpanded ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    gap: 0.5,
                  }}
                >
                  {isExpanded ? <CloseIcon fontSize="small" /> : <BarChartIcon fontSize="small" />}
                  {isExpanded ? 'Hide Stats' : 'View Stats'}
                </IconButton>
              </Tooltip>
          </Box>
        </Box>
        
        {/* Expandable Chart Section */}
        <Collapse in={isExpanded}>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 2 }}>
            <ProspectEventsLinearChart 
              streamUuid={stream.key} 
              enabled={isExpanded}
              height={300}
            />
          </Box>
        </Collapse>

        {/* Withdraw Confirmation Dialog */}
        <Dialog
          open={isWithdrawDialogOpen}
          onClose={() => setIsWithdrawDialogOpen(false)}
          onClick={(e) => e.stopPropagation()}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Withdraw all pending invites</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1">
              This will withdraw all pending connection invites for this experiment.
              A background job will process each pending invite and withdraw it.
              Once the withdrawal is complete, the experiment status will be set to <strong>Done</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone. Are you sure you want to proceed?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsWithdrawDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleWithdrawConfirm}
              disabled={updateStatusMutation.isPending}
            >
              Withdraw invites
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

